"use client";

import { signIn } from "next-auth/react";
import { useForm } from "react-hook-form";
import { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Eye, EyeOff, AlertCircle, Loader2, ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { handleError, handleNotification } from "@/lib/utils/auxiliars";

/* ── Brand panel (left side, desktop) ────────────────────── */
function BrandPanel() {
  return (
    <div className="relative hidden lg:flex lg:flex-col lg:w-[45%] xl:w-[42%] min-h-screen overflow-hidden bg-[#050d1a]">
      {/* Gradient blobs */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-[#2563eb] opacity-[0.13] blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-[#1d4ed8] opacity-[0.10] blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full bg-[#3b82f6] opacity-[0.07] blur-[80px]" />
      </div>
      {/* Subtle grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(248,250,252,.6) 1px,transparent 1px),linear-gradient(90deg,rgba(248,250,252,.6) 1px,transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      {/* Large semi-transparent watermark logo — same style as landing section 2 */}
      <div
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          backgroundImage: "url('/landing/orkestria-full-cream.png')",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center 58%",
          backgroundSize: "min(70vh, 640px) auto",
          opacity: 0.055,
          maskImage: "radial-gradient(closest-side, black 40%, transparent 88%)",
          WebkitMaskImage: "radial-gradient(closest-side, black 40%, transparent 88%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full px-12 py-10">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#2563eb]/20 border border-[#2563eb]/30 flex items-center justify-center">
            <Image
              src="/landing/orkestria-mark-light.png"
              alt="Orkestria"
              width={18}
              height={18}
              className="object-contain"
            />
          </div>
          <span className="text-[#f8fafc] font-semibold text-[15px] tracking-tight">
            Orkestria
          </span>
        </div>

        {/* Center content — vertically centered */}
        <div className="flex-1 flex flex-col justify-center">
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#2563eb]/30 bg-[#2563eb]/10 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#60a5fa] animate-pulse" />
              <span className="text-[11px] font-medium text-[#93c5fd] tracking-wide uppercase">
                Sistema activo
              </span>
            </div>
            <h1 className="text-[#f8fafc] font-semibold text-[36px] xl:text-[40px] leading-[1.05] tracking-tight mb-4">
              Orquesta tu<br />
              <span className="text-[#60a5fa]">restaurante</span>.
            </h1>
            <p className="text-[#94a3b8] text-[15px] leading-relaxed max-w-[340px]">
              Pedidos, cocina, caja, facturación y carta digital — sincronizados en tiempo real.
            </p>
          </div>

          {/* Feature list */}
          <ul className="space-y-3">
            {[
              "Operación en tiempo real",
              "Facturación y caja integradas",
              "Analítica accionable",
              "Carta digital personalizable",
            ].map((f) => (
              <li key={f} className="flex items-center gap-3 text-[#cbd5e1] text-[13.5px]">
                <span className="w-4 h-4 rounded-full bg-[#2563eb]/20 border border-[#2563eb]/40 flex items-center justify-center flex-none">
                  <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                    <path d="M1.5 5 L4 7.5 L8.5 2.5" stroke="#60a5fa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <p className="text-[#475569] text-[11px]">
          © 2026 Orkestria · <a href="/" className="hover:text-[#94a3b8] transition-colors">criskop.com</a>
        </p>
      </div>
    </div>
  );
}

/* ── Main login form (needs Suspense for useSearchParams) ─── */
function LoginPageContent() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [notice, setNotice] = useState(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const invitationToken = searchParams.get("invitationToken");
  const sessionExpired = searchParams.get("expired") === "1";

  const registerHref = invitationToken
    ? `/auth/register?invitationToken=${invitationToken}`
    : "/auth/register";

  // Si llegamos aquí por sesión expirada (interceptor 401 del axios),
  // mostrar aviso amigable. Solo en el primer render — se limpia al tipear.
  useEffect(() => {
    if (sessionExpired) {
      setNotice({
        type: "info",
        text: "Tu sesión expiró. Inicia sesión nuevamente para continuar.",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionExpired]);

  const values = watch();
  useEffect(() => {
    if (notice && (values?.email || values?.password)) setNotice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.email, values?.password]);

  const onSubmit = handleSubmit(async (data) => {
    setNotice(null);
    setLoading(true);

    try {
      const res = await signIn("credentials", {
        redirect: false,
        email: String(data.email).trim(),
        password: String(data.password ?? ""),
        callbackUrl: invitationToken
          ? `/auth/invitation?token=${invitationToken}`
          : "/dashboard",
      });

      if (res?.error) {
        const message =
          res.error === "CredentialsSignin"
            ? "Credenciales inválidas. Verifica tu correo y contraseña."
            : res.error || "Error al iniciar sesión. Intenta nuevamente.";
        setNotice({ type: "error", text: message });
        handleNotification("error", "Error al iniciar sesión", message);
        setLoading(false);
        return;
      }

      if (res?.ok) {
        router.push(
          invitationToken
            ? `/auth/invitation?token=${invitationToken}`
            : "/dashboard",
        );
      } else {
        setLoading(false);
      }
    } catch (err) {
      handleError(
        err,
        "Error al iniciar sesión. Intenta nuevamente.",
        undefined,
        setLoading,
        setNotice,
      );
    }
  });

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 sm:px-10 min-h-screen lg:min-h-0 bg-background">
      <div className="w-full max-w-[400px]">
        {/* Mobile logo */}
        <div className="flex items-center gap-2.5 mb-8 lg:hidden">
          <div className="w-7 h-7 rounded-md bg-primary/15 border border-primary/25 flex items-center justify-center">
            <Image
              src="/landing/orkestria-mark-light.png"
              alt="Orkestria"
              width={14}
              height={14}
              className="object-contain opacity-90 dark:opacity-100"
              style={{ filter: "var(--ork-mark-filter, none)" }}
            />
          </div>
          <span className="font-semibold text-[14px] text-foreground tracking-tight">
            Orkestria
          </span>
        </div>

        {/* Heading */}
        <div className="mb-7">
          <h1 className="text-[22px] font-semibold tracking-tight text-foreground mb-1.5">
            Bienvenido de vuelta
          </h1>
          <p className="text-[13.5px] text-muted-foreground">
            Ingresa tus credenciales para acceder al panel.
          </p>
        </div>

        {/* Error notice */}
        {notice && (
          <div
            className={`flex items-start gap-2.5 p-3 rounded-[9px] text-[13px] mb-5 ${
              notice.type === "error"
                ? "bg-destructive/10 text-destructive border border-destructive/20"
                : "bg-primary/10 text-primary border border-primary/20"
            }`}
          >
            <AlertCircle className="w-4 h-4 mt-px flex-none" />
            <span>{notice.text}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-[12.5px] font-medium text-foreground/80">
              Correo electrónico
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="tucorreo@ejemplo.com"
              autoComplete="email"
              className="h-10 text-[13.5px]"
              {...register("email", { required: "El correo es requerido" })}
            />
            {errors.email && (
              <span className="text-[12px] text-destructive">{errors.email.message}</span>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-[12.5px] font-medium text-foreground/80">
              Contraseña
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPwd ? "text" : "password"}
                placeholder="••••••••"
                autoComplete="current-password"
                className="h-10 text-[13.5px] pr-10"
                {...register("password", { required: "La contraseña es requerida" })}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full w-10 hover:bg-transparent cursor-pointer"
                onClick={() => setShowPwd((v) => !v)}
                aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showPwd ? (
                  <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
                ) : (
                  <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                )}
              </Button>
            </div>
            {errors.password && (
              <span className="text-[12px] text-destructive">{errors.password.message}</span>
            )}
          </div>

          <Button
            type="submit"
            className="w-full h-10 text-[13.5px] font-medium cursor-pointer mt-1"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                Iniciando sesión...
              </>
            ) : (
              <>
                Iniciar sesión
                <ArrowRight className="ml-2 h-3.5 w-3.5" />
              </>
            )}
          </Button>
        </form>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border/60" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-background px-3 text-[11px] uppercase tracking-widest text-muted-foreground/60">
              O
            </span>
          </div>
        </div>

        {/* Register link */}
        <p className="text-center text-[13px] text-muted-foreground">
          ¿No tienes cuenta?{" "}
          <Link
            href={registerHref}
            className="text-primary font-medium hover:underline underline-offset-2 transition-colors"
          >
            Regístrate
          </Link>
        </p>

        {/* Footer */}
        <p className="text-center text-[11px] text-muted-foreground/50 mt-8">
          © {new Date().getFullYear()} Orkestria
        </p>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <div className="flex min-h-screen w-full bg-background">
      <BrandPanel />
      <Suspense
        fallback={
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        }
      >
        <LoginPageContent />
      </Suspense>
    </div>
  );
}
